import { amountToDisplayValue, Flex, IconButton, Space, Text, Tooltip } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccount } from '@features/accounts/hooks/useAccount'
import type { Posting } from '@features/transactions/types/posting'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useIsTruncated, useSegment } from '../../../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import * as Styled from './styles'

interface BookingLineProps {
  posting: Posting
}

export const BookingLine = ({ posting }: BookingLineProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { organization } = useUserOrganization()

  const { originatorType } = useTransactionDrawerContext()
  const { accountId, amount, currencyId, id: postingId, side, transactionId } = posting

  const { account } = useAccount(accountId)

  const [accountNameRef, isAccountNameTruncated] = useIsTruncated(account?.name ?? '')

  const formattedAmount = amountToDisplayValue(amount, false, currencyId)

  const handleHoverButtonClick = useCallback(() => {
    if (organization?.url) {
      const accountUrl = getOrganizationViewUrl(
        organization.url,
        reactRoute.organization.accounts.getAccountRoute(accountId),
      )
      window.open(accountUrl, '_blank')
    }
    track('xxx - Bookkeeping - Ledger - Drawer', {
      action: 'Open posting',
      postingId,
      transactionId,
      transactionType: originatorType,
    })
  }, [accountId, organization?.url, originatorType, postingId, track, transactionId])

  const accountNameElement = (
    <Text nowrap truncate ref={accountNameRef}>
      {account?.name}
    </Text>
  )

  return (
    <Styled.BookingWrapper>
      <Styled.TextWrapper>
        {isAccountNameTruncated ? (
          <Tooltip label={account?.name} placement="top">
            {accountNameElement}
          </Tooltip>
        ) : (
          accountNameElement
        )}

        <Text variant="bodySM" colorVariant="disable">
          {account?.accountNo}
        </Text>
      </Styled.TextWrapper>
      <Space horizontal />
      <Flex.Item flexShrink={0}>
        <Flex flexDirection="column" alignItems="flex-end">
          <Text nowrap colorVariant={side === 'debit' ? 'success' : 'danger'}>
            {formattedAmount}
          </Text>
          <Text nowrap variant="bodySM" colorVariant="disable">
            {t(side).toLowerCase()}
          </Text>
        </Flex>
      </Flex.Item>
      <Styled.HoverButtonContainer>
        <IconButton icon="squareArrowOutUpRight" onClick={handleHoverButtonClick} />
      </Styled.HoverButtonContainer>
    </Styled.BookingWrapper>
  )
}
